import PublicPageShell, { FeatureGrid, SectionHeading } from '@/components/PublicPageShell';
import {
  ShieldCheck, Heart, Activity, Dumbbell, Gauge, ClipboardList, BookOpen, BarChart3,
  Search, Video, MessageSquare, FileText, Map as MapIcon, Wallet, Brain,
  Lock, Compass, KeyRound, Trophy, Zap, Sparkles, Smartphone,
} from 'lucide-react';

const PERFORMANCE = [
  { icon: ShieldCheck, t: 'Verified Player Profiles', d: 'Multi-layer credential & coach verification.' },
  { icon: Heart,       t: 'Injury History & Medical Records', d: 'POPIA-compliant tracking with consent controls.' },
  { icon: Gauge,       t: 'Player Development Score (PDS)', d: 'Dynamic, comparable growth rating per player.' },
  { icon: Activity,    t: 'Wellness & Recovery Monitoring', d: 'Daily check-ins and load context for coaches.' },
  { icon: Dumbbell,    t: 'Training Program Builder', d: 'Periodized, position-specific conditioning.' },
  { icon: ClipboardList, t: 'Acute/Chronic Load Management', d: 'GPS-integration ready for institutions.' },
  { icon: BookOpen,    t: 'Individual Development Plans (IDPs)', d: 'Goals + coach feedback per player.' },
  { icon: FileText,    t: 'Digital Playbook & Tactical Board', d: 'Animated set-pieces and shareable plays.' },
  { icon: BarChart3,   t: 'Performance Benchmarking', d: 'National percentile rankings by position & age.' },
  { icon: Search,      t: 'Opposition Analysis Module', d: 'Shareable scouting reports for matchdays.' },
  { icon: Video,       t: 'Video Analysis & Telestration', d: 'Shareable clip libraries for selectors.' },
];

const OPERATIONS = [
  { icon: MessageSquare, t: 'Team Communication Hub', d: 'Squad chats, announcements, parent portal.' },
  { icon: ClipboardList, t: 'Digital Match Sheets', d: 'Real-time stats and season leaderboards.' },
  { icon: MapIcon,       t: 'National Talent Map', d: 'Geographic visualisation of every verified player.' },
  { icon: Brain,         t: 'XV Assist AI', d: 'Persistent SA-rugby AI mentor — periodised programmes, position-specific drills, role-aware advice.' },
  { icon: Lock,          t: 'SuperAdmin Platform', d: 'Live analytics, audit logging, override controls.' },
  { icon: KeyRound,      t: 'Role-Based Access Control', d: 'Granular, secure, auditable permissions.' },
];

export default function Features() {
  return (
    <PublicPageShell
      eyebrow="PLATFORM FEATURES & TECHNOLOGY"
      title="A complete operating system for South African rugby"
      intro="From individual player development to national talent mapping — every tool every stakeholder needs, on one mobile-first platform."
      dataTestId="features-page"
    >
      <SectionHeading eyebrow="PLAYER & PERFORMANCE">Player &amp; performance features</SectionHeading>
      <FeatureGrid items={PERFORMANCE} cols="md:grid-cols-2 lg:grid-cols-3" />

      <SectionHeading eyebrow="OPERATIONS & ADMIN">Operations &amp; administration features</SectionHeading>
      <FeatureGrid items={OPERATIONS} cols="md:grid-cols-2 lg:grid-cols-3" />

      {/* v7.3 §50 — Competitive differentiators */}
      <SectionHeading eyebrow="WHY FUTUREXV">What no other platform offers</SectionHeading>
      <p className="text-zinc-400 mb-6 max-w-3xl">
        Built for South African rugby — load-shedding aware, multi-language UI, POPIA-aligned by design. Ten capabilities you won&rsquo;t find on CrewLAB, TeamSnap, TrainingPeaks or Hudl.
      </p>
      <FeatureGrid
        items={[
          { icon: Gauge,     t: 'Scout-facing Player Development Score', d: 'Verified, coach-validated PDS surfaced directly to professional scouts. The platform’s primary value proposition.' },
          { icon: Sparkles,  t: 'SA Rugby Season Calendar', d: 'XV Assist auto-aligns to SA Pre/Early/Peak/Off/Transition phases — Craven Week and provincial windows built in.' },
          { icon: Zap,       t: 'Load-Shedding Adaptation', d: '“No power today” flag swaps gym cards for body-weight alternatives. No other sports platform addresses this.' },
          { icon: ShieldCheck, t: 'Mandatory Rugby Warm-Up Gate', d: 'Scientifically validated injury-prevention protocol — required on every training card before skills/conditioning unlock.' },
          { icon: Brain,     t: 'Phase Two Skill Cards', d: 'Position-specific (Jackal for flankers, scrum body position for props, box kick for scrumhalves) with SA context notes.' },
          { icon: Smartphone, t: 'Data-Light Mode', d: 'Text-only cards and 48-hour offline cache for township and rural players on limited mobile data.' },
          { icon: MapIcon,   t: 'Transport-Barrier RSVP', d: 'Explicit “Transport (no lift)” absence option gives coaches actionable data on a genuine SA grassroots barrier.' },
          { icon: Lock,      t: 'Children’s Act 38 Alignment', d: 'SA-specific safeguarding compliance that international platforms don’t address.' },
        ]}
        cols="md:grid-cols-2 lg:grid-cols-3"
      />

      <div className="mt-16 bg-[#0a0a0a] border border-zinc-800 rounded-2xl overflow-hidden" data-testid="partner-callout">
        <a href="https://tryandscore.co.za" target="_blank" rel="noopener noreferrer" className="block">
          <img src="/tryandscore_logo.jpg" alt="TryAndScore Sports — World of Rugby" className="w-full h-auto block" />
        </a>
        <div className="p-8 md:p-10">
          <p className="text-[#A3FF00] text-xs font-bold tracking-[0.25em] mb-2">OFFICIAL PARTNER</p>
          <h3 className="text-xl md:text-2xl font-heading font-bold">TryAndScore Sports / World of Rugby</h3>
          <p className="text-zinc-400 mt-2 leading-relaxed">
            All registered FutureXV users receive a <strong className="text-[#A3FF00]">5% discount at checkout</strong> on all rugby goods.
            Applies to players, school programmes, clubs, and varsity teams — making real rugby equipment more accessible
            for every user on the platform.
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="inline-block bg-[#A3FF00]/10 border border-[#A3FF00]/30 text-[#A3FF00] font-bold rounded-md px-3 py-1.5 text-sm">5% OFF</span>
            <span className="text-zinc-300 text-sm">Use code at checkout: <span className="font-mono font-bold text-[#A3FF00]" data-testid="features-promo-code">FUTUREXV</span></span>
          </div>
          <a
            href="https://tryandscore.co.za"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 border-2 border-[#A3FF00] text-[#A3FF00] hover:bg-[#A3FF00] hover:text-black font-bold px-6 py-2.5 rounded-md transition-colors"
            data-testid="features-shop-now-link"
          >
            <Trophy className="w-4 h-4" />
            Visit tryandscore.co.za
          </a>
        </div>
      </div>
    </PublicPageShell>
  );
}
